<script lang="ts" setup>
import {
  Back,
  Document,
  Finished,
  OfficeBuilding,
  Right,
} from "@element-plus/icons-vue";
import { computed, toRef } from "vue";
import { HousingApplication, HousingSiteResultType, Workflow } from "~/api";
import { splitByCaps } from "~/utils/miscUtils";
import { useHousingAplicationWizard } from "~/YYC_Housing/composables/useHousingApplicationWizard";
import { ApplicationWizardStep } from "../types/plusTypes";

const props = defineProps<{
  step: number;
  showNext?: boolean;
  showPrev?: boolean;
  simple?: boolean;
  application?: HousingApplication;
  workflow?: Workflow;
  showCurrentStepOnly?: boolean;
  iconBackground?: string;
}>();

const { stepStatusMap, disabledSteps, handleGotoStep } =
  useHousingAplicationWizard(
    toRef(props, "application"),
    toRef(props, "workflow"),
  );

// For simplicity, all steps that are not yet completed will have the same status. We can enhance this in the future if needed.
const computeStatus = (
  _step: number,
): "" | "success" | "wait" | "error" | "finish" | "process" => {
  return "process";
};

const getNextStep = (): string => {
  let nextStep = props.step + 1;
  if (nextStep > 2) {
    nextStep = 2;
  }

  return splitByCaps(ApplicationWizardStep[nextStep]);
};

const getPrevStep = (): string => {
  const prevStep = props.step - 1;

  return splitByCaps(ApplicationWizardStep[prevStep]);
};

const locationsSelected = computed(
  () =>
    props.application?.siteResults
      ?.filter(
        (preference, index, self) =>
          index ===
          self.findIndex(
            (p) =>
              p.siteCode === preference.siteCode && p.hmb === preference.hmb,
          ),
      )
      .filter((site) => site.resultType === HousingSiteResultType.PREFERRED)
      .length,
);

const handleClickNext = () => {
  let nextStep = props.step + 1;
  if (nextStep >= ApplicationWizardStep.ReviewAndSubmit) {
    nextStep = ApplicationWizardStep.ReviewAndSubmit;
  }

  handleGotoStep(nextStep);
};

const handleClickPrev = () => {
  let prevStep = props.step - 1;
  if (prevStep <= 0) {
    prevStep = 0;
  }

  if (disabledSteps.value.includes(prevStep)) {
    return;
  }

  handleGotoStep(prevStep);
};
</script>

<template>
  <div
    class="applicationWizardSteps"
    :class="{
      simple: simple,
      showingCurrentStepOnly: showCurrentStepOnly,
      'no-step-buttons': !showNext && !showPrev,
    }"
  >
    <div v-if="showPrev && step > 0" class="prev-btn">
      <el-button
        v-if="!disabledSteps.includes(step - 1)"
        color="#f5f5f5"
        size="large"
        :icon="Back"
        @click="handleClickPrev"
      />
      <!-- On mobile view, the names of previous steps should be displayed as text -->
      <el-text
        v-if="showCurrentStepOnly && !disabledSteps.includes(step - 1)"
        class="step-text"
      >
        {{ getPrevStep() }}
      </el-text>

      <!-- Home text -->
      <el-text v-else-if="getPrevStep() === 'Home'" class="step-text">
        Home
      </el-text>

      <!-- Display "Previous" text if steps are shown on WizardSteps bar -->
      <el-text
        v-else-if="
          !disabledSteps.includes(step - 1) &&
          getPrevStep() !== 'Home' &&
          !showCurrentStepOnly
        "
        class="step-text"
      >
        Previous
      </el-text>
    </div>

    <el-steps :active="step + 1" align-center finish-status="success">
      <el-step
        v-if="
          showCurrentStepOnly
            ? step === ApplicationWizardStep.SelectLocations
            : true
        "
        :icon="OfficeBuilding"
        :status="computeStatus(ApplicationWizardStep.SelectLocations)"
        :class="{
          disabled: disabledSteps.includes(
            ApplicationWizardStep.SelectLocations,
          ),
        }"
        @click="() => handleGotoStep(ApplicationWizardStep.SelectLocations)"
      >
        <template #title>
          <div
            :class="{
              currentStep: step === ApplicationWizardStep.SelectLocations,
            }"
          >
            Select Properties
          </div>
        </template>

        <template #description>
          <span v-if="locationsSelected !== undefined && locationsSelected > 0"
            >{{ locationsSelected }} Selected</span
          >
          <span v-else>None Selected</span>
        </template>
      </el-step>
      <el-step
        v-if="
          showCurrentStepOnly
            ? step === ApplicationWizardStep.ApplicationForm
            : true
        "
        :icon="Document"
        :status="computeStatus(ApplicationWizardStep.ApplicationForm)"
        :class="{
          disabled: disabledSteps.includes(
            ApplicationWizardStep.ApplicationForm,
          ),
        }"
        @click="() => handleGotoStep(ApplicationWizardStep.ApplicationForm)"
      >
        <template #title>
          <div
            :class="{
              currentStep: step === ApplicationWizardStep.ApplicationForm,
            }"
            :title="application?.applicantName"
          >
            Application Form
          </div>
        </template>

        <template #description>
          <div>
            {{
              stepStatusMap[ApplicationWizardStep.ApplicationForm]
                .statusDisplayText
            }}
          </div>
        </template>
      </el-step>

      <el-step
        v-if="
          showCurrentStepOnly
            ? step === ApplicationWizardStep.ReviewAndSubmit
            : true
        "
        :icon="Finished"
        :status="computeStatus(ApplicationWizardStep.ReviewAndSubmit)"
        :class="{
          disabled: disabledSteps.includes(
            ApplicationWizardStep.ReviewAndSubmit,
          ),
        }"
        @click="() => handleGotoStep(ApplicationWizardStep.ReviewAndSubmit)"
      >
        <template #title>
          <div
            :class="{
              currentStep: step === ApplicationWizardStep.ReviewAndSubmit,
            }"
          >
            Review & Submit
          </div>
        </template>

        <template #description>
          <div>
            {{
              stepStatusMap[ApplicationWizardStep.ReviewAndSubmit]
                .statusDisplayText
            }}
          </div>
        </template>
      </el-step>
    </el-steps>

    <div
      v-if="showNext && !(step >= ApplicationWizardStep.ReviewAndSubmit)"
      class="next-btn"
    >
      <el-button
        v-if="!disabledSteps.includes(step + 1)"
        type="primary"
        size="large"
        :icon="Right"
        @click="handleClickNext"
      >
      </el-button>
      <el-text
        v-if="
          showCurrentStepOnly && step !== ApplicationWizardStep.ReviewAndSubmit
        "
        class="step-text"
      >
        {{ getNextStep() }}
      </el-text>

      <el-text v-else class="step-text"> Next </el-text>
    </div>
  </div>
</template>

<style lang="less">
.applicationWizardSteps {
  display: grid;
  grid-template-areas: "prev steps next";
  grid-template-columns: 1fr 5fr 1fr;
  align-items: start;
  // grid-gap: 1em;
  // margin-bottom: 1em;
  padding: 1em;
  // box-sizing: border-box;
  border-radius: 5px;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: #82675336 0px 0px 5px 1px;

  @media (max-width: 1025px) {
    margin-bottom: 1em;
  }
  &.no-step-buttons {
    grid-template-areas: "steps";
    grid-template-columns: 1fr;
  }

  .next-btn {
    grid-area: next;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5em;
    .el-text {
      align-self: end;
      text-align: right;
    }
  }
  .prev-btn {
    grid-area: prev;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5em;
    .el-text {
      align-self: start;
      text-align: left;
    }
  }

  @media (max-width: 600px) {
    .next-btn .el-text,
    .prev-btn .el-text {
      font-size: 0.7em;
    }
  }

  @media (max-width: 450px) {
    .next-btn .el-text,
    .prev-btn .el-text {
      font-size: 0.6em;
    }
  }

  .el-steps {
    grid-area: steps;
    justify-content: center;

    @media (max-width: 650px) {
      font-size: 0.75em;
    }
    width: 100%;

    &.el-steps--simple {
      padding: 1em 2em;
    }

    .currentStep {
      font-size: 1.2em;
      white-space: nowrap;
      text-align: center;
      @media (max-width: 650px) {
        font-size: 0.9em;
      }
    }

    .el-step.is-simple:not(:last-of-type) .el-step__title {
      max-width: 100%;
    }

    .el-step__icon {
      top: -0.4em;
      background: v-bind(iconBackground);
      // var(--yych-background-color);
    }
    .el-step {
      cursor: pointer;
      @iconColor: var(--el-color-primary);

      &.disabled {
        cursor: not-allowed;
      }

      .el-step__arrow {
        display: none;
      }

      .el-step__description {
        font-size: 0.9em;
        color: var(--el-color-info);
        padding: 0;
      }

      &:has(.currentStep) {
        .el-step__icon {
          // display: none;
          // --> if we do show it, we need to set the color too
          &.is-text {
            color: @iconColor;
          }
        }
        .el-step__title {
          color: @iconColor;
          &.is-success {
            color: @iconColor;
          }
        }
        .el-step__description {
          color: @iconColor;
        }
        .el-step__head {
          border-color: @iconColor;
          &.is-success {
            border-color: @iconColor;
          }
        }
      }
    }
  }
  &.simple {
    // background-color: var(--el-fill-color-light);
    background-color: var(--yych-background-color);
    box-shadow: 0px 0px 6px 4px hsla(0, 0%, 0%, 0.15);
  }
  &.showingCurrentStepOnly {
    grid-template-columns: 1fr max-content 1fr;
    grid-template-rows: repeat(2, auto);
    // grid-template-areas:
    //   "prev x next"
    //   "steps steps steps";

    @media screen and (max-width: 425px) {
      .step-text {
        font-size: 0.45em;
      }
    }
  }
}
</style>
